import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { OBSERVABILITY_EVENTS } from "@workspace/observability";
import { track } from "@workspace/observability/web";
import { t } from "@workspace/strings/web";
import { useToast } from "@/hooks/use-toast";
import { createCommandTimeoutTracker } from "@/lib/realtime-command-timeout";
import {
  useRealtime,
  type CommandAck,
  type CommandResult,
  type DeviceLiveStatus,
  type DeviceNowPlaying,
  type LiveDevice,
  type SignalMessage,
  type SyncGroupCommandAck,
} from "@/hooks/use-realtime";

/** How long the console waits for a device's command-result before assuming
 * the player is a zombie (socket open, JS frozen) and toasting. */
const COMMAND_RESPONSE_TIMEOUT_MS = 5_000;

/**
 * Machine reason keys players put in a failed command-result's `error` field
 * (see player-core PlayOutcome), translated here for the operator's toast.
 * Unknown strings pass through verbatim (older players send prose).
 */
const COMMAND_REASON_TEXT: Record<string, string> = {
  channelNotActive: t("playerUi.commandReason.channelNotActive"),
  nothingScheduled: t("playerUi.commandReason.nothingScheduled"),
  noScreenMedia: t("playerUi.commandReason.noScreenMedia"),
  takeoverActive: t("playerUi.commandReason.takeoverActive"),
  liveAnnouncementActive: t("playerUi.commandReason.liveAnnouncementActive"),
  emptyPlaylist: t("playerUi.commandReason.emptyPlaylist"),
  historyEmpty: t("playerUi.commandReason.historyEmpty"),
  notHandled: t("playerUi.commandReason.notHandled"),
};

export type LiveRealtimeSubscriber = {
  onLiveMessage?: (data: Record<string, unknown>) => void;
  onSignal?: (signal: SignalMessage) => void;
  onBroadcastEnded?: (reason: string) => void;
};

type RealtimeContextValue = {
  connected: boolean;
  onlineUsers: string[];
  liveDevices: LiveDevice[];
  deviceStatuses: Record<string, DeviceLiveStatus>;
  nowPlaying: Record<string, DeviceNowPlaying>;
  sendCommand: (deviceId: string, command: string, args?: unknown) => boolean;
  sendSyncGroupCommand: (
    syncGroupId: string,
    command: string,
    args?: unknown,
  ) => boolean;
  sendSignal: (deviceId: string, payload: unknown) => void;
  sendLiveMessage: (message: Record<string, unknown>) => boolean;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);
const LiveSubscriberContext = createContext<
  ((subscriber: LiveRealtimeSubscriber) => () => void) | null
>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const liveSubscribersRef = useRef(new Set<LiveRealtimeSubscriber>());

  // One timer per in-flight commandId; fires the "no response" toast when a
  // sent command gets neither a delivered:false ack nor a command-result.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const pendingCommandsRef = useRef(
    createCommandTimeoutTracker({
      timeoutMs: COMMAND_RESPONSE_TIMEOUT_MS,
      onTimeout: () => {
        toastRef.current({
          title: t("webMisc.realtime.commandTimeout.title"),
          description: t("webMisc.realtime.commandTimeout.description"),
          variant: "destructive",
        });
      },
    }),
  );
  useEffect(() => {
    const tracker = pendingCommandsRef.current;
    return () => tracker.clear();
  }, []);

  const notifyLiveMessage = useCallback((data: Record<string, unknown>) => {
    for (const sub of liveSubscribersRef.current) {
      sub.onLiveMessage?.(data);
    }
  }, []);

  const notifySignal = useCallback((signal: SignalMessage) => {
    for (const sub of liveSubscribersRef.current) {
      sub.onSignal?.(signal);
    }
  }, []);

  const notifyBroadcastEnded = useCallback((reason: string) => {
    for (const sub of liveSubscribersRef.current) {
      sub.onBroadcastEnded?.(reason);
    }
  }, []);

  const base = useRealtime({
    onCommandResult: (res: CommandResult) => {
      // The device answered: whatever the verdict, the command is no longer
      // pending a response. settle() is also the tab filter — command-results
      // fan out to every control peer in the workspace, and a failure toast
      // belongs only in the tab that actually sent the command.
      const wasMine = pendingCommandsRef.current.settle(res.commandId);
      if (res.ok || !wasMine) return;
      const raw =
        typeof res.result?.error === "string" ? res.result.error : null;
      const err = raw
        ? COMMAND_REASON_TEXT[raw] ?? raw
        : t("webMisc.realtime.commandFailed.fallback");
      toast({ title: t("webMisc.realtime.commandFailed.title"), description: err, variant: "destructive" });
    },
    onCommandAck: (ack: CommandAck) => {
      if (!ack.delivered) {
        // Nothing will ever answer: settle now so the not-connected toast is
        // the only feedback (a delivered:true ack keeps the timer running
        // until the device's command-result arrives). Acks are unicast to the
        // sender, but gate on settle() anyway so a replayed/foreign ack can
        // never toast a tab that sent nothing.
        const wasMine = pendingCommandsRef.current.settle(ack.commandId);
        if (!wasMine) return;
        toast({
          title: t("webMisc.realtime.deviceNotConnected.title"),
          description: t("webMisc.realtime.deviceNotConnected.description"),
          variant: "destructive",
        });
      }
    },
    onSyncGroupCommandAck: (ack: SyncGroupCommandAck) => {
      if (ack.delivered === 0) {
        toast({
          title: t("webMisc.realtime.syncGroupNotDelivered.title"),
          description: t("webMisc.realtime.syncGroupNotDelivered.description"),
          variant: "destructive",
        });
      }
    },
    onGatewayError: (error: string) => {
      toast({ title: t("webMisc.realtime.channelError.title"), description: error, variant: "destructive" });
    },
    onLiveMessage: notifyLiveMessage,
    onSignal: notifySignal,
    onBroadcastEnded: notifyBroadcastEnded,
  });

  // Show a "reconnecting" affordance only after we've connected at least once,
  // so the badge doesn't flash during the initial connect.
  const everConnectedRef = useRef(false);
  if (base.connected) everConnectedRef.current = true;
  const reconnecting = everConnectedRef.current && !base.connected;

  const registerLiveSubscriber = useCallback((subscriber: LiveRealtimeSubscriber) => {
    liveSubscribersRef.current.add(subscriber);
    return () => {
      liveSubscribersRef.current.delete(subscriber);
    };
  }, []);

  const sendCommand = useCallback(
    (deviceId: string, command: string, args?: unknown): boolean => {
      const commandId = base.sendCommand(deviceId, command, args);
      if (commandId) {
        // Arm the no-response timeout; a delivered:false ack or the device's
        // command-result settles it (see the useRealtime handlers above).
        pendingCommandsRef.current.start(commandId);
        track(OBSERVABILITY_EVENTS["remote.command_sent"], {
          device_id: deviceId,
          command,
        });
      } else {
        toast({
          title: t("webMisc.realtime.notConnected.title"),
          description: t("webMisc.realtime.notConnected.description"),
          variant: "destructive",
        });
      }
      return commandId !== null;
    },
    [base.sendCommand, toast],
  );

  const sendSyncGroupCommand = useCallback(
    (syncGroupId: string, command: string, args?: unknown): boolean => {
      const sent = base.sendSyncGroupCommand(syncGroupId, command, args);
      if (sent) {
        track(OBSERVABILITY_EVENTS["remote.command_sent"], {
          sync_group_id: syncGroupId,
          command,
        });
      }
      if (!sent) {
        toast({
          title: t("webMisc.realtime.notConnected.title"),
          description: t("webMisc.realtime.notConnected.description"),
          variant: "destructive",
        });
      }
      return sent;
    },
    [base.sendSyncGroupCommand, toast],
  );

  const value: RealtimeContextValue = {
    ...base,
    sendCommand,
    sendSyncGroupCommand,
  };

  return (
    <LiveSubscriberContext.Provider value={registerLiveSubscriber}>
      <RealtimeContext.Provider value={value}>
        {children}
        {reconnecting && (
          <div
            role="status"
            aria-live="polite"
            data-testid="realtime-reconnecting"
            className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-md"
          >
            <span className="size-2 animate-pulse rounded-full bg-amber-500" />
            {t("webMisc.realtime.reconnecting")}
          </div>
        )}
      </RealtimeContext.Provider>
    </LiveSubscriberContext.Provider>
  );
}

export function useRealtimeContext(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error("useRealtimeContext must be used within RealtimeProvider");
  }
  return ctx;
}

export function useLiveRealtimeSubscriber(subscriber: LiveRealtimeSubscriber): void {
  const register = useContext(LiveSubscriberContext);
  if (!register) {
    throw new Error("useLiveRealtimeSubscriber must be used within RealtimeProvider");
  }
  const onLiveMessage = subscriber.onLiveMessage;
  const onSignal = subscriber.onSignal;
  const onBroadcastEnded = subscriber.onBroadcastEnded;
  useEffect(() => {
    return register({ onLiveMessage, onSignal, onBroadcastEnded });
  }, [register, onLiveMessage, onSignal, onBroadcastEnded]);
}

export type { DeviceLiveStatus, DeviceNowPlaying, LiveDevice, SignalMessage };
